import React, { useMemo, useState, useEffect } from "react";
import { useDictionary } from "@/components/DictProvider/DictProvider";
import { useSearchParams } from "next/navigation";
import ColorVariantCircle from "./ColorVariantCircle";

interface Color {
  name: string;
  type: string;
  value: string[];
  thumbnail?: string | null;
}

interface ProductVariant {
  color?: Color;
  productSlug: string;
  size?: string;
}

interface ProductSelectionProps {
  variants: ProductVariant[];
  title?: String;
  onVariantSelect: (variant: ProductVariant) => void;
  setThumbnailImage: any;
}

export default function ProductSelection({
  variants,
  title,
  onVariantSelect,
  setThumbnailImage,
}: ProductSelectionProps) {
  const dict = useDictionary();
  const searchParams = useSearchParams();

  // State to track selected size and color
  const [selectedSize, setSelectedSize] = useState<string | null>(null);
  const [selectedColor, setSelectedColor] = useState<string | null>(null);

  const [visibleSize, setVisibleSize] = useState(
    () => Number(sessionStorage?.getItem("visibleSize")) || 6
  );
  const [visibleColors, setVisibleColors] = useState(
    () => Number(sessionStorage?.getItem("visibleColors")) || 6
  );

  // Memoized computation of sizes and colors
  const { sizeColorMap, sizes, hasColors, hasSizes } = useMemo(() => {
    const sizeColorMap = new Map<string, ProductVariant[]>();
    let hasSizes = false;
    let hasColors = false;

    variants.forEach((variant) => {
      if (variant?.size) {
        hasSizes = true;
        if (!sizeColorMap?.has(variant?.size)) {
          sizeColorMap?.set(variant?.size, []);
        }
        sizeColorMap?.get(variant?.size)!.push(variant);
      }

      if (
        variant?.color &&
        !Array.isArray(variant?.color) &&
        typeof variant?.color === "object" &&
        Object.keys(variant?.color).length > 0
      ) {
        hasColors = true;
      }
    });

    const sizes = Array.from(sizeColorMap.keys());
    return { sizeColorMap, sizes, hasColors, hasSizes };
  }, [variants]);

  const colors = useMemo(() => {
    if (!hasSizes) {
      return variants?.filter((v) => v?.color).map((v) => v?.color!);
    }
    if (!selectedSize) return [];
    return (sizeColorMap.get(selectedSize) || [])
      .filter((v) => v?.color)
      .map((v) => v?.color!);
  }, [selectedSize, sizeColorMap, hasSizes, variants]);

  // Helper functions for selection
  const selectFirstAvailable = () => {
    if (hasSizes) {
      const firstSize = sizes[0];
      const variantsForSize = sizeColorMap?.get(firstSize) || [];
      const firstColorVariant = variantsForSize?.find((v) => v?.color);

      setSelectedSize(firstSize);
      if (firstColorVariant) {
        setSelectedColor(firstColorVariant?.color!.name);
        onVariantSelect(firstColorVariant);
      } else if (variantsForSize?.length > 0) {
        setSelectedColor(null);
        onVariantSelect(variantsForSize[0]);
      }
    } else if (hasColors) {
      const firstColor = variants?.find((v) => v?.color);
      setSelectedSize(null);
      setSelectedColor(firstColor?.color?.name || null);
      if (firstColor) {
        onVariantSelect(firstColor);
      }
    } else {
      onVariantSelect(variants[0]);
    }
  };

  const handleSizeSelect = (size: string) => {
    setSelectedSize(size);
    const variantsForSize = sizeColorMap?.get(size) || [];
    const firstColorVariant = variantsForSize?.find((v) => v?.color);

    if (firstColorVariant) {
      setSelectedColor(firstColorVariant?.color!.name);
      onVariantSelect(firstColorVariant);
    } else if (variantsForSize?.length > 0) {
      setSelectedColor(null);
      onVariantSelect(variantsForSize[0]);
    }
  };

  const handleColorSelect = (colorName: string) => {
    setSelectedColor(colorName);
    const selectedVariant = hasSizes
      ? (sizeColorMap?.get(selectedSize!) || [])?.find(
          (v) => v?.color?.name === colorName
        )
      : variants?.find((v) => v?.color?.name === colorName);

    if (selectedVariant) {
      onVariantSelect(selectedVariant);
    }
  };

  // Initialize default selection on mount
  useEffect(() => {
    const sizeParam = searchParams?.get("size");
    const colorParam = searchParams?.get("color");
    if (sizeParam || colorParam) {
      if (sizeParam) {
        setSelectedSize(decodeURIComponent(sizeParam));
      }
      if (colorParam) {
        setSelectedColor(decodeURIComponent(colorParam)); // Set color by name
      }
    } else {
      selectFirstAvailable();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const toggleSize = () => {
    if (visibleSize === 6) {
      setVisibleSize(sizes?.length);
      sessionStorage.setItem("visibleSize", sizes?.length?.toString());
    } else {
      setVisibleSize(6);
      sessionStorage.removeItem("visibleSize");
    }
  };

  const toggleColors = () => {
    if (visibleColors === 6) {
      setVisibleColors(colors?.length);
      sessionStorage.setItem("visibleColors", colors?.length?.toString());
    } else {
      setVisibleColors(6);
      sessionStorage.removeItem("visibleColors");
    }
  };

  return (
    <div className="space-y-4 my-4">
      {hasSizes && (
        <div className="w-full flex flex-col justify-start items-start gap-4">
          <div className="flex justify-start items-center gap-1">
            <p className="text-sm lg:text-lg font-semibold text-black">
              {/* {dict.poductDescriptionPage.selectSize} */}
              {dict?.poductDescriptionPage?.select}{" "}
              {title || dict?.poductDescriptionPage?.size}
            </p>
            :
            <span className="text-sm lg:text-lg text-own-black">
              {selectedSize}
            </span>
          </div>
          <div className="flex flex-wrap gap-4">
            {sizes?.slice(0, visibleSize)?.map((size) => (
              <button
                key={size}
                onClick={() => handleSizeSelect(size)}
                className={`px-4 py-2 border-2 font-semibold ${
                  selectedSize === size
                    ? "border-primary text-primary"
                    : "border-secondary text-black"
                }`}
              >
                {size}
              </button>
            ))}
            {sizes?.length > 6 && (
              <div
                onClick={toggleSize}
                className="mt-2 text-primary font-[500] hover:underline cursor-pointer"
              >
                {visibleSize === 6 ? `${sizes?.length - 6}+ More` : "Show Less"}
              </div>
            )}
          </div>
        </div>
      )}

      {hasColors && (
        <div className="w-full">
          <div className="flex justify-start items-center gap-1 mb-4">
            <p className="text-sm lg:text-lg font-semibold text-black">
              {dict.poductDescriptionPage.selectColor}
            </p>
            :
            <span className="text-sm lg:text-lg text-own-black">
              {selectedColor}
            </span>
          </div>
          <div className="flex flex-wrap gap-4">
            {colors?.slice(0, visibleColors)?.map((color) => (
              <button
                key={color?.name}
                onClick={() => handleColorSelect(color?.name)}
                onMouseEnter={() => setThumbnailImage(color?.thumbnail)}
                onMouseLeave={() => setThumbnailImage(null)}
              >
                <ColorVariantCircle
                  name={color?.name}
                  type={color?.type?.toLocaleLowerCase()}
                  value={color?.value}
                  selectedColor={selectedColor}
                />
              </button>
            ))}
            {colors?.length > 6 && (
              <div
                onClick={toggleColors}
                className="mt-2 text-primary font-[500] hover:underline cursor-pointer"
              >
                {visibleColors === 6
                  ? `${colors?.length - 6}+ More`
                  : "Show Less"}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
